// File: src/app/AppShell.tsx
import React from 'react'
import { SidebarProvider, SidebarInset, SidebarTrigger } from '../components/ui/sidebar'

type Role = 'admin' | 'mitarbeitende' | 'system_admin'

type AppShellProps = {
  title: string
  role: Role
  setRole: (r: Role) => void
  canSwitchRole?: boolean
  onSystemAdminHome?: () => void
  onLogout?: () => void
  onOpenSearch?: () => void
  authed?: boolean
  orgLogoUrl?: string | null
  orgLogoAlt?: string
  sidebar?: React.ReactNode
  showSidebar?: boolean
  headerExtras?: React.ReactNode
  trialBanner?: React.ReactNode
  children?: React.ReactNode
}

export function AppShell({ title, role, setRole, canSwitchRole = false, onSystemAdminHome, onLogout, onOpenSearch, authed, orgLogoAlt, sidebar, showSidebar = true, headerExtras, trialBanner, children }: AppShellProps){
  void title
  return (
    <SidebarProvider>
      <div className="h-screen w-full bg-slate-50 text-slate-900 overflow-hidden">
        <div className="w-full h-screen p-0">
          <div className="flex w-full h-screen bg-slate-50 overflow-hidden">
            {showSidebar && sidebar}
            <SidebarInset>
              {authed && trialBanner}
              {authed && (
                <header className="sticky top-0 z-20 flex h-[62px] items-center gap-3 border-b border-[var(--line)] bg-white px-4 md:px-[26px]">
                  {showSidebar && <SidebarTrigger className="md:hidden" aria-label="Menü öffnen" />}
                  {onOpenSearch && (
                    <button
                      type="button"
                      onClick={onOpenSearch}
                      data-tour="global-search"
                      aria-label="Suchen (Strg + K)"
                      className="flex h-10 w-full max-w-[400px] items-center gap-2.5 rounded-[9px] border border-[var(--line)] bg-[var(--surface)] px-3.5 text-left text-[.84rem] text-[var(--ink-muted)] transition hover:border-[var(--teal-mid)] focus-visible:border-[var(--teal)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[rgba(0,159,181,.1)]"
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
                      <span className="truncate">VA, Kapitel, Mitarbeitende suchen…</span>
                      <span className="ml-auto hidden rounded-[6px] border border-[var(--line-soft)] px-1.5 py-px text-[.68rem] text-[var(--ink-muted)] lg:inline">Strg K</span>
                    </button>
                  )}
                  <div className="ml-auto flex items-center gap-2.5">
                    {orgLogoAlt && (
                      <div className="hidden items-center gap-2 rounded-[9px] border border-[var(--line)] px-3 py-[7px] text-[.79rem] font-semibold text-[var(--ink-mid)] sm:flex">
                        <span className="h-2 w-2 rounded-full" style={{ background: 'var(--green)' }} aria-hidden="true" />
                        <span className="max-w-[180px] truncate">{orgLogoAlt}</span>
                      </div>
                    )}
                    {headerExtras}
                    {canSwitchRole && (
                      <select
                        aria-label="Rolle wählen"
                        value={role}
                        onChange={(e) => setRole(e.target.value as Role)}
                        className="h-10 rounded-[9px] border border-[var(--line)] bg-white px-2 text-[.8rem] text-[var(--ink-mid)] focus:outline-none"
                      >
                        <option value="admin">Admin</option>
                        <option value="mitarbeitende">Mitarbeitende*r</option>
                        <option value="system_admin">System Admin</option>
                      </select>
                    )}
                    {onSystemAdminHome && (
                      <button type="button" className="rounded-[9px] px-3 py-1.5 text-[.8rem] text-[var(--teal-dark)] hover:bg-[var(--teal-light)]" onClick={onSystemAdminHome}>Zurück</button>
                    )}
                    <button
                      type="button"
                      onClick={onLogout}
                      aria-label="Abmelden"
                      title="Abmelden"
                      className="grid h-10 w-10 place-items-center rounded-[9px] border border-[var(--line)] bg-white text-[var(--ink-mid)] transition hover:border-[var(--teal-mid)] hover:text-[var(--teal-dark)]"
                    >
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5M21 12H9" /></svg>
                    </button>
                  </div>
                </header>
              )}
              <main
                className="flex-1 overflow-y-scroll overflow-x-hidden p-4 md:p-6"
                style={{ scrollbarGutter: "stable" }}
                role="main"
                aria-label="Hauptinhalt"
              >
                {children}
              </main>
            </SidebarInset>
          </div>
        </div>
      </div>
    </SidebarProvider>
  )
}

export default AppShell
